import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  SxProps,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";

import Gogi from "../assets/gogi_bear.png";
import customScrollbarStyle from "../constants/CustomScrollbarStyle";
import {
  NOTIFICATION_ID,
  NotificationMetaData,
} from "../constants/Notification";
import { getNotificationDetails } from "../firebase/popup";
import { Notification } from "../interfaces/Notification";
import User from "../interfaces/User";
import Button from "./Button";
import { SwipeableDrawer } from "./swipeable-drawer";
import Typography from "./Typography";

type Props = {
  open: boolean;
  body?: JSX.Element;
  handleClose: () => void;
  button1Function?: () => void;
  button2Function?: () => void;
  user: User;
  notificationId: NOTIFICATION_ID;
  metaData?: NotificationMetaData; // Add metadata prop
  sx?: SxProps;
};

// Helper function to populate notification metadata for title and body
const populateNotificationData = (
  notification: Notification,
  metaData?: NotificationMetaData
): Notification => {
  const populatedNotification = { ...notification };

  // Use passed metadata if available, otherwise use notification's metadata
  const dataToUse = metaData || notification.meta_data;

  if (!dataToUse) {
    return populatedNotification;
  }

  // Populate title
  if (populatedNotification.title) {
    dataToUse.forEach((data) => {
      populatedNotification.title = populatedNotification.title.replace(
        `{${data.key}}`,
        data.value || ""
      );
    });
    // Remove any remaining template keys
    populatedNotification.title = populatedNotification.title.replace(
      /{.*?}/g,
      ""
    );
  }

  // Populate body
  if (populatedNotification.body) {
    dataToUse.forEach((data) => {
      populatedNotification.body = populatedNotification.body.replace(
        `{${data.key}}`,
        data.value || ""
      );
    });
    // Remove any remaining template keys
    populatedNotification.body = populatedNotification.body.replace(
      /{.*?}/g,
      ""
    );
  }

  return populatedNotification;
};

const PopupComponent = ({
  user,
  open,
  body,
  button1Function,
  button2Function,
  handleClose,
  notificationId,
  metaData, // Add metadata parameter
  sx,
}: Props) => {
  const theme = useTheme();
  const [notificationData, setNotificationData] = useState<
    Notification | undefined
  >();
  const [populatedNotificationData, setPopulatedNotificationData] = useState<
    Notification | undefined
  >();
  const isMdSize = useMediaQuery("(min-width:900px)");

  useEffect(() => {
    if (open) {
      const getNotification = async () => {
        const notification = await getNotificationDetails(notificationId);
        if (notification) {
          setNotificationData(notification as Notification);
          // Populate the notification with dynamic variables using passed metadata
          const populated = populateNotificationData(
            notification as Notification,
            metaData
          );
          setPopulatedNotificationData(populated);
        }
      };
      getNotification();
    }
  }, [open, metaData]); // Add metaData to dependency array

  const MainComponent = () => {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          position: "relative",
        }}
      >
        {handleClose && (
          <IconButton
            sx={{
              display: { md: "block", xs: "none" },
              position: "absolute",
              right: { md: "10px", xs: "10px" },
              top: { md: "8px", xs: "8px" },
            }}
          >
            <CloseIcon
              onClick={handleClose}
              sx={{ m: "5px", cursor: "pointer" }}
            />
          </IconButton>
        )}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "50%",
            height: "100%",
          }}
        >
          <Box
            sx={{
              backgroundImage: `url(${
                notificationData?.imageUrl
                  ? notificationData?.imageUrl
                  : Gogi.src
              })`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              height: "100%",
              width: "100%",
            }}
          />
        </Box>
        <Box
          sx={{
            height: "100%",
            overflowY: "auto",
            ...customScrollbarStyle,
            padding: 4,
            width: {
              xs: "100%",
              md: "50%",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
            justifyContent="space-between"
          >
            <Box sx={{ mt: "10px" }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Avatar
                  sx={{
                    height: "133px",
                    width: "133px",
                    background: theme.whiteBg.WBG3,
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: "100%",
                      backgroundImage: `url(${Gogi.src})`,
                      backgroundPosition: "center",
                      backgroundSize: "cover",
                      backgroundRepeat: "no-repeat",
                    }}
                  />
                </Avatar>
              </Box>
            </Box>
            <Box>
              <DialogTitle>
                <Typography
                  variant={"h2"}
                  color={theme.brand2[500]}
                  sx={{ textAlign: "center" }}
                >
                  {populatedNotificationData && populatedNotificationData.title}
                </Typography>
              </DialogTitle>
              <DialogContent
                sx={{
                  textAlign: "center",
                  padding: "0 40px",
                  margin: "10px 0",
                  overflowY: "clip",
                }}
              >
                <DialogContentText>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    {populatedNotificationData && (
                      <Typography
                        dangerouslySetInnerHTML={{
                          __html: populatedNotificationData.body,
                        }}
                        variant={"title2"}
                        color={theme.brand1[500]}
                      />
                    )}
                    {body}
                  </Box>
                </DialogContentText>
              </DialogContent>
            </Box>
            <Box>
              <DialogActions
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  paddingX: "5px",
                }}
              >
                {populatedNotificationData &&
                  populatedNotificationData.button1Text && (
                    <Button
                      onClick={button1Function}
                      sx={{ marginBottom: "10px" }}
                    >
                      {populatedNotificationData.button1Text}
                    </Button>
                  )}
                {populatedNotificationData &&
                  populatedNotificationData.button2Text && (
                    <Button
                      onClick={button2Function}
                      sx={{ marginBottom: "10px" }}
                    >
                      {populatedNotificationData.button2Text}
                    </Button>
                  )}
              </DialogActions>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  if (populatedNotificationData) {
    if (isMdSize) {
      return (
        <Dialog
          sx={{
            marginBottom: 0,
            textAlign: "center",
            ...sx,
          }}
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              marginLeft: 0,
              marginRight: 0,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              boxShadow: "none",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 0,
              marginTop: { md: "0", xs: "10vh" },
              maxWidth: { md: "100vw", xs: "100%" },
              maxHeight: { md: "100vh", xs: "100%" },
              height: { md: "80vh", xs: "90vh" },
              width: { md: "60vw", xs: "100vw" },
              minWidth: { md: "800px" },
            },
          }}
        >
          <MainComponent />
        </Dialog>
      );
    } else {
      return (
        <SwipeableDrawer open={open} onClose={handleClose} onOpen={() => {}}>
          <MainComponent />
        </SwipeableDrawer>
      );
    }
  } else {
    return <></>;
  }
};

export default PopupComponent;
